import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { service_open_pose } from '@/services/open_pose/service_open_pose';
import { LOCAL__ui_set_number } from '@/state/ui/_base/LOCAL__ui_set_number';
import { assert_not_null } from '@shared/asserts/utils_asserts';
import { useRef } from 'react';
import * as THREE from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EOpenPoseBodyGroup } from '../enums/e_open_pose_body_group';
import { EOpenPoseInteractionMode } from '../enums/e_open_pose_interaction_mode';
import { use_open_pose_billboard_texture } from '../open_pose_billboard/use_open_pose_billboard_texture';
import { use_open_pose_body } from '../open_pose_body/use_open_pose_body';

export function use_open_pose_editor() {
  /*
   * FIELDS
   */
  const dispatch = use_dispatch();
  const { is_service_open_pose_init, open_pose_body_size } = use_slice_ui(['is_service_open_pose_init', 'open_pose_body_size']);

  const ref_is_camera_perspective = useRef(true);

  const body_controller = use_open_pose_body();

  const { texture: billboard_texture, width: billboard_width, height: billboard_height } = use_open_pose_billboard_texture();

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (!is_service_open_pose_init) return;

    const bound_tick = (time_ms: number) => service_open_pose.camera_controller.tick(time_ms);
    service_open_pose.start_main_render_loop(bound_tick);

    return () => service_open_pose.stop_main_render_loop();
  }, [is_service_open_pose_init]);

  use_effect(() => {
    if (!is_service_open_pose_init) return;

    service_open_pose.add_dom_event_listener('mousedown', on_mouse_down);
    service_open_pose.add_dom_event_listener('mousemove', on_mouse_move);
    service_open_pose.add_dom_event_listener('mouseup', on_mouse_up);

    return () => {
      service_open_pose.remove_dom_event_listener('mousedown', on_mouse_down);
      service_open_pose.remove_dom_event_listener('mousemove', on_mouse_move);
      service_open_pose.remove_dom_event_listener('mouseup', on_mouse_up);
    };
  }, [body_controller, is_service_open_pose_init]);

  use_effect(() => {
    if (!is_service_open_pose_init) return;

    ref_is_camera_perspective.current = service_open_pose.camera_controller.is_camera_perspective;

    const torso = service_open_pose.scene.children.find(object => object.name === EOpenPoseBodyGroup.TORSO);
    assert_not_null(torso);

    const body_world_position = torso.get_world_position_zero_offset();

    service_open_pose.camera_controller.reset_perspective_in_front_of(body_world_position);
    service_open_pose.camera_controller.set_camera_into_scene_current();

    if (service_open_pose.camera_controller.is_camera_perspective) service_open_pose.camera_controller.enable_first_person_control();

    service_open_pose.camera_controller.update_camera_projection();

    service_open_pose.transform_controls.detach();
  }, [is_service_open_pose_init]);

  // Initialize body size to default value if not set
  use_effect(() => {
    if (!is_service_open_pose_init) return;
    if (open_pose_body_size === null || open_pose_body_size === undefined) {
      console.log('[USE_OPEN_POSE_EDITOR] Initializing body size to default:', ConstantsOpenPose.DEFAULT_BODY_SIZE);
      dispatch(LOCAL__ui_set_number({ key: 'open_pose_body_size', value: ConstantsOpenPose.DEFAULT_BODY_SIZE }));

      // Give some time for body to be created, then store base positions
      // setTimeout(() => {
      //   if (body_controller.reset_scene) {
      //     // This will trigger store_base_positions through reset_scene
      //     // But we don't actually want to reset, just store positions
      //     console.log('[USE_OPEN_POSE_EDITOR] Body initialized, base positions stored');
      //   }
      // }, 500);
    }
  }, [is_service_open_pose_init, open_pose_body_size, dispatch]);

  use_effect(() => {
    if (!is_service_open_pose_init) return;

    service_open_pose.billboard_controller.set_is_perspective_camera(ref_is_camera_perspective.current);
    service_open_pose.billboard_controller.update_black_background_visibility();
    service_open_pose.camera_controller.update_camera_projection();
  }, [ref_is_camera_perspective.current, is_service_open_pose_init]);

  use_effect(() => {
    if (!is_service_open_pose_init) return;

    if (billboard_width === 0 && billboard_height === 0) return;

    //todo: extend ts type
    if ((billboard_texture as any).image) {
      const texture = billboard_texture as THREE.Texture;
      service_open_pose.billboard_controller.update_billboard(texture, billboard_width, billboard_height);
    }
  }, [billboard_texture, billboard_width, billboard_height, is_service_open_pose_init]);

  // Handle body size changes from slider
  use_effect(() => {
    if (!is_service_open_pose_init) return;
    if (!open_pose_body_size || open_pose_body_size <= 0) return;

    // Add a small delay to ensure body controller is fully initialized
    const timer = setTimeout(() => {
      console.log('[USE_OPEN_POSE_EDITOR] Applying body size:', open_pose_body_size);
      body_controller.apply_body_size_dinamic(open_pose_body_size);
    }, 100);

    return () => clearTimeout(timer);
  }, [open_pose_body_size, is_service_open_pose_init, body_controller]);

  /*
   * EVENTS
   */

  function on_mouse_down(event: MouseEvent) {
    body_controller.on_mouse_down(event, service_open_pose.get_current_canvas());
  }

  function on_mouse_move(event: MouseEvent) {
    body_controller.on_mouse_move(event, service_open_pose.get_current_canvas());
  }

  function on_mouse_up(event: MouseEvent) {
    body_controller.on_mouse_up(event, service_open_pose.get_current_canvas());
  }

  /*
   * METHODS
   */

  function toggle_camera() {
    service_open_pose.camera_controller.toggle_camera();

    const is_camera_perspective_now = service_open_pose.camera_controller.is_camera_perspective;
    ref_is_camera_perspective.current = is_camera_perspective_now;

    const torso = service_open_pose.scene.children.find(object => object.name === EOpenPoseBodyGroup.TORSO);
    assert_not_null(torso);

    if (is_camera_perspective_now) {
      const body_world_position = torso.get_world_position_zero_offset();
      service_open_pose.camera_controller.reset_perspective_in_front_of(body_world_position);
      service_open_pose.camera_controller.set_camera_into_scene_current();
      service_open_pose.camera_controller.enable_first_person_control();
    } else service_open_pose.camera_controller.disable_first_person_control();

    service_open_pose.camera_controller.update_camera_projection();
    service_open_pose.billboard_controller.update_black_background_visibility();
  }

  function reset_body() {
    console.log('[RESET_BODY] Starting body reset...');

    try {
      // Reset body controller scene first (this resets body pose and position)
      console.log('[RESET_BODY] Calling body_controller.reset_scene()...');
      body_controller.reset_body();
      console.log('[RESET_BODY] Body controller scene reset completed');

      // Don't touch camera - only reset body pose and position
      console.log('[RESET_BODY] Body reset completed successfully - camera unchanged');
    } catch (error) {
      console.error('[RESET_BODY] Error during body reset:', error);
      throw error;
    }
  }

  function reset_camera() {
    const torso = service_open_pose.scene.children.find(object => object.name === EOpenPoseBodyGroup.TORSO);
    assert_not_null(torso);

    if (service_open_pose.camera_controller.is_camera_perspective) {
      const body_world_position = torso.get_world_position_zero_offset();
      service_open_pose.camera_controller.reset_perspective_in_front_of(body_world_position);
      service_open_pose.camera_controller.set_camera_into_scene_current();
    }

    service_open_pose.camera_controller.update_camera_projection();
    service_open_pose.billboard_controller.update_black_background_visibility();
  }

  function reset_pose() {}

  function set_interaction_mode(mode: EOpenPoseInteractionMode) {
    console.log('[USE_OPEN_POSE_EDITOR] set_interaction_mode called with mode:', mode);
    console.log('[USE_OPEN_POSE_EDITOR] body_controller:', body_controller);
    body_controller.set_interaction_mode(mode);
    console.log('[USE_OPEN_POSE_EDITOR] set_interaction_mode completed');
  }

  function apply_body_size(size_percent: number) {
    body_controller.apply_body_size_dinamic(size_percent);
  }

  /*
   * RETURN API
   */

  return {
    toggle_camera,
    reset_camera,
    reset_body,
    reset_pose,
    set_interaction_mode,
    apply_body_size,
  };
}
